import { History, LocationState } from 'history'

export const NAV_STACK_STORAGE_KEY = 'cc-scheduler:nav-stack'
export const NAV_STACK_TTL_MS = 15 * 60 * 1000

export interface NavigationStackEntry {
  url: string
  label: string
}

interface NavigationStackState {
  stack: NavigationStackEntry[]
  ts: number
}

const readStackState = (maxAgeMs = NAV_STACK_TTL_MS): NavigationStackState | null => {
  try {
    const raw = sessionStorage.getItem(NAV_STACK_STORAGE_KEY)
    if (!raw) {
      return null
    }

    const state = JSON.parse(raw) as NavigationStackState
    if (!state?.ts || Date.now() - state.ts > maxAgeMs) {
      sessionStorage.removeItem(NAV_STACK_STORAGE_KEY)
      return null
    }

    return state
  } catch {
    sessionStorage.removeItem(NAV_STACK_STORAGE_KEY)
    return null
  }
}

const writeStackState = (stack: NavigationStackEntry[]) => {
  sessionStorage.setItem(
    NAV_STACK_STORAGE_KEY,
    JSON.stringify({
      stack,
      ts: Date.now(),
    }),
  )
}

export const pushNavigationStack = (entry: NavigationStackEntry) => {
  const state = readStackState()
  const stack = state?.stack ?? []
  const last = stack[stack.length - 1]

  if (last?.url === entry.url && last?.label === entry.label) {
    return
  }

  stack.push(entry)
  writeStackState(stack)
}

export const getBackTarget = (): NavigationStackEntry | null => {
  const state = readStackState()
  if (!state?.stack.length) {
    return null
  }

  return state.stack[state.stack.length - 1]
}

export const popBackTarget = (): NavigationStackEntry | null => {
  const state = readStackState()
  if (!state?.stack.length) {
    return null
  }

  const entry = state.stack.pop() as NavigationStackEntry
  writeStackState(state.stack)
  return entry
}

export const getCurrentReturnUrl = (history: History) => {
  const { pathname, search } = history.location
  return `${pathname}${search}`
}

export const navigateWithStack = (history: History, to: string, backLabel: string, state?: LocationState) => {
  pushNavigationStack({
    url: getCurrentReturnUrl(history),
    label: backLabel,
  })
  history.push(to, state)
}
